/**
 * Predictive search for hero and header search bars.
 * Attaches to every input with [data-predictive-search]; no-ops if none found.
 *
 * Responsibilities:
 *   1. Fetches & caches the locale-specific intent dictionary (JSON asset).
 *   2. On every keystroke (debounced 300 ms, min 2 chars):
 *        a) Looks up the typed query against the intent index (prefix match).
 *        b) Fires Shopify's /search/suggest.json for live product results.
 *        c) Renders a two-section dropdown: Suggestions + Products.
 *        d) If 0 products come back and an intent matched, renders rescue mode
 *           (quiz CTA + curated fallback product links).
 *   3. Full keyboard navigation (↑ ↓ Enter Esc Tab).
 *   4. ARIA: aria-expanded, role=listbox, role=option, aria-activedescendant.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. CONSTANTS                                                        */
  /* ------------------------------------------------------------------ */

  var DEBOUNCE_MS          = 300;
  var MIN_QUERY_LEN        = 2;
  var MAX_PRODUCT_RESULTS  = 5;

  // Inline SVGs — copied from snippets/icon.liquid (search, close, chevron-right).
  // Sized at 20px; dropdown .icon size constrained in CSS to override critical.css.
  var ICON_CHEVRON_RIGHT =
    '<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="9 18 15 12 9 6"></polyline></svg>';

  /* ------------------------------------------------------------------ */
  /* 2. SHARED INTENTS CACHE (by URL)                                    */
  /* ------------------------------------------------------------------ */

  var intentsCache = {};

  function loadIntentsShared(intentsUrl) {
    if (intentsCache[intentsUrl]) return Promise.resolve(intentsCache[intentsUrl]);
    if (!intentsUrl) {
      intentsCache[intentsUrl] = { _index: {} };
      return Promise.resolve(intentsCache[intentsUrl]);
    }
    return fetch(intentsUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        intentsCache[intentsUrl] = data;
        return data;
      })
      .catch(function () {
        intentsCache[intentsUrl] = { _index: {} };
        return intentsCache[intentsUrl];
      });
  }

  /* ------------------------------------------------------------------ */
  /* 3. GLOBAL UTILITIES (accent strip, escape)                          */
  /* ------------------------------------------------------------------ */

  var ACCENT_MAP = {
    '\u00e0':'a','\u00e1':'a','\u00e2':'a','\u00e3':'a','\u00e4':'a',
    '\u00e8':'e','\u00e9':'e','\u00ea':'e','\u00eb':'e',
    '\u00ec':'i','\u00ed':'i','\u00ee':'i','\u00ef':'i',
    '\u00f2':'o','\u00f3':'o','\u00f4':'o','\u00f5':'o','\u00f6':'o',
    '\u00f9':'u','\u00fa':'u','\u00fb':'u','\u00fc':'u',
    '\u00f1':'n','\u00e7':'c','\u00fd':'y','\u00ff':'y'
  };
  var ACCENT_RE = /[\u00e0-\u00e4\u00e8-\u00eb\u00ec-\u00ef\u00f2-\u00f6\u00f9-\u00fc\u00f1\u00e7\u00fd\u00ff]/g;

  function stripAccents(str) {
    return str.toLowerCase().replace(ACCENT_RE, function (c) {
      return ACCENT_MAP[c] || c;
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ------------------------------------------------------------------ */
  /* 4. FETCH PREDICTIVE (shared)                                        */
  /* ------------------------------------------------------------------ */

  function fetchPredictive(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
              '&resources[type]=product&limit=' + MAX_PRODUCT_RESULTS;
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return (data.resources && data.resources.results && data.resources.results.products)
          ? data.resources.results.products
          : [];
      })
      .catch(function () { return []; });
  }

  /* ------------------------------------------------------------------ */
  /* 5. INIT ONE PREDICTIVE SEARCH INSTANCE                              */
  /* ------------------------------------------------------------------ */

  function initPredictiveSearch(input) {
    var searchWrapper = input.closest('[data-predictive-search-wrapper]');
    if (!searchWrapper) searchWrapper = input.closest('.hero__search-wrapper');
    if (!searchWrapper) return;

    var searchForm = input.closest('form');
    var quizUrl    = input.getAttribute('data-quiz-url') || '';
    var intentsUrl = input.getAttribute('data-intents-url') || '';
    var labels = {
      suggestions      : input.getAttribute('data-label-suggestions')       || 'Suggestions',
      products         : input.getAttribute('data-label-products')          || 'Results',
      viewAll          : input.getAttribute('data-label-view-all')          || 'View all results',
      quizCta          : input.getAttribute('data-label-quiz-cta')          || '',
      suggestedProducts: input.getAttribute('data-label-suggested-products')|| ''
    };
    var ariaLabel = input.getAttribute('data-aria-label') || 'Search suggestions';

    var optionIdPrefix = (input.id || 'predictive') + '-option';
    var state = {
      isOpen        : false,
      activeIndex   : -1,
      items         : [],
      debounceTimer : null
    };
    var dropdown = null;

    function findIntentGroup(intentsData, query) {
      if (!intentsData || !intentsData._index) return null;
      var index = intentsData._index;
      var stripped = stripAccents(query);
      if (stripped.length < MIN_QUERY_LEN) return null;
      var exactMatch = null, prefixMatch = null, prefixLen = Infinity;
      var keys = Object.keys(index);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i], strippedKey = stripAccents(key);
        if (strippedKey === stripped) { exactMatch = key; break; }
        if (strippedKey.indexOf(stripped) === 0 && strippedKey.length < prefixLen) {
          prefixMatch = key;
          prefixLen = strippedKey.length;
        }
      }
      var winningKey = exactMatch || prefixMatch;
      if (!winningKey) return null;
      var groupKey = index[winningKey], group = intentsData[groupKey];
      return group ? { key: groupKey, group: group } : null;
    }

    function buildDropdownHtml(suggestions, products, rescueData) {
      var html = '<div class="search-dropdown" role="listbox" aria-label="' + escapeHtml(ariaLabel) + '">';
      if (suggestions && suggestions.length > 0) {
        html += '<div class="search-dropdown__section">';
        html += '<div class="search-dropdown__section-label">' + escapeHtml(labels.suggestions) + '</div>';
        for (var s = 0; s < suggestions.length; s++) {
          html += '<a href="' + escapeHtml(suggestions[s].url) + '" class="search-dropdown__item search-dropdown__item--suggestion" ' +
            'role="option" id="' + optionIdPrefix + '-s' + s + '" aria-selected="false">' +
            ICON_CHEVRON_RIGHT + '<span class="search-dropdown__item-text">' + escapeHtml(suggestions[s].label) + '</span></a>';
        }
        html += '</div>';
      }
      if (products && products.length > 0) {
        html += '<div class="search-dropdown__section">';
        html += '<div class="search-dropdown__section-label">' + escapeHtml(labels.products) + '</div>';
        for (var p = 0; p < products.length; p++) {
          var prod = products[p];
          var imgHtml = (prod.image && prod.image.src)
            ? '<img class="search-dropdown__item-img" src="' + escapeHtml(prod.image.src) + '" alt="" width="48" height="48" loading="lazy">'
            : '';
          html += '<a href="' + escapeHtml(prod.url) + '" class="search-dropdown__item search-dropdown__item--product" ' +
            'role="option" id="' + optionIdPrefix + '-p' + p + '" aria-selected="false">' + imgHtml +
            '<div class="search-dropdown__item-info">' +
            '<span class="search-dropdown__item-title">' + escapeHtml(prod.title) + '</span>' +
            '<span class="search-dropdown__item-price">' + escapeHtml(prod.price) + '</span></div></a>';
        }
        html += '</div>';
      }
      if (rescueData) {
        html += '<div class="search-dropdown__section search-dropdown__section--rescue">';
        if (rescueData.quizUrl) {
          html += '<a href="' + escapeHtml(rescueData.quizUrl) + '" class="search-dropdown__rescue-cta">' +
            '<span class="search-dropdown__rescue-cta-text">' + escapeHtml(labels.quizCta) + '</span>' + ICON_CHEVRON_RIGHT + '</a>';
        }
        if (rescueData.products && rescueData.products.length > 0) {
          html += '<div class="search-dropdown__rescue-label">' + escapeHtml(labels.suggestedProducts) + '</div>';
          for (var f = 0; f < rescueData.products.length; f++) {
            var fb = rescueData.products[f];
            html += '<a href="/products/' + escapeHtml(fb.handle) + '" class="search-dropdown__item search-dropdown__item--fallback" ' +
              'role="option" id="' + optionIdPrefix + '-f' + f + '" aria-selected="false">' +
              '<span class="search-dropdown__item-text">' + escapeHtml(fb.title) + '</span>' + ICON_CHEVRON_RIGHT + '</a>';
          }
        }
        html += '</div>';
      }
      html += '<div class="search-dropdown__footer">';
      html += '<a href="/search?q=' + encodeURIComponent(input.value) + '" class="search-dropdown__footer-link" ' +
        'role="option" id="' + optionIdPrefix + '-footer" aria-selected="false">' + escapeHtml(labels.viewAll) + ' ' + ICON_CHEVRON_RIGHT + '</a>';
      html += '</div></div>';
      return html;
    }

    // Check if we're inside the mobile drawer
    var isInDrawer = !!searchWrapper.closest('.header__drawer');

    function ensureDropdown() {
      if (dropdown) return;
      dropdown = document.createElement('div');
      dropdown.className = 'search-dropdown-container';
      if (isInDrawer) {
        // Inside drawer: append to wrapper so it stays in drawer's stacking context
        searchWrapper.appendChild(dropdown);
      } else {
        // Outside drawer: append to <body> so dropdown escapes overflow:hidden ancestors
        // (the .hero section clips children). Positioned via fixed coords in showDropdown.
        document.body.appendChild(dropdown);
      }
    }

    function rebuildItemsList() {
      state.items = [];
      if (!dropdown) return;
      var all = dropdown.querySelectorAll('[role="option"]');
      for (var i = 0; i < all.length; i++) state.items.push(all[i]);
    }

    function positionDropdown() {
      if (!dropdown) return;
      if (isInDrawer) {
        // Inside drawer: use relative positioning (CSS handles it)
        dropdown.style.position = 'relative';
        dropdown.style.top = '';
        dropdown.style.left = '';
        dropdown.style.width = '100%';
      } else {
        // Outside drawer: use fixed positioning based on input or form
        var targetEl = searchForm || input;
        var rect = targetEl.getBoundingClientRect();
        dropdown.style.position = 'fixed';
        dropdown.style.top   = (input.getBoundingClientRect().bottom + 6) + 'px';   // 6 px gap (≈ --space-2)
        dropdown.style.left  = rect.left + 'px';
        dropdown.style.width = rect.width + 'px';
      }
    }

    function showDropdown(html) {
      ensureDropdown();
      dropdown.innerHTML = html;
      dropdown.style.display = 'block';
      positionDropdown();
      input.setAttribute('aria-expanded', 'true');
      state.isOpen = true;
      rebuildItemsList();
    }

    function hideDropdown() {
      if (dropdown) dropdown.style.display = 'none';
      input.setAttribute('aria-expanded', 'false');
      state.isOpen = false;
      state.activeIndex = -1;
      state.items = [];
      input.removeAttribute('aria-activedescendant');
    }

    function setActiveIndex(index) {
      if (state.activeIndex >= 0 && state.items[state.activeIndex]) {
        state.items[state.activeIndex].setAttribute('aria-selected', 'false');
        state.items[state.activeIndex].classList.remove('is-active');
      }
      state.activeIndex = index;
      if (index >= 0 && state.items[index]) {
        state.items[index].setAttribute('aria-selected', 'true');
        state.items[index].classList.add('is-active');
        input.setAttribute('aria-activedescendant', state.items[index].id);
        state.items[index].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function handleKeydown(e) {
      if (!state.isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          triggerSearch();
        }
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (state.items.length) setActiveIndex((state.activeIndex + 1) % state.items.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (state.items.length) setActiveIndex(state.activeIndex <= 0 ? state.items.length - 1 : state.activeIndex - 1);
          break;
        case 'Enter':
          if (state.activeIndex >= 0 && state.items[state.activeIndex]) {
            e.preventDefault();
            var href = state.items[state.activeIndex].getAttribute('href');
            if (href) window.location.href = href;
          }
          hideDropdown();
          break;
        case 'Escape':
          e.preventDefault();
          hideDropdown();
          input.focus();
          break;
        case 'Tab':
          hideDropdown();
          break;
      }
    }

    function triggerSearch() {
      if (state.debounceTimer) clearTimeout(state.debounceTimer);
      var query = input.value.trim();
      if (query.length < MIN_QUERY_LEN) return;
      loadIntentsShared(intentsUrl).then(function (intentsData) {
        var intentMatch = findIntentGroup(intentsData, query);
        var suggestions = intentMatch ? intentMatch.group.suggestions : [];
        fetchPredictive(query).then(function (products) {
          var rescueData = null;
          if (products.length === 0 && intentMatch) {
            rescueData = {
              products: intentMatch.group.fallback_products || [],
              quizUrl: quizUrl
            };
          }
          showDropdown(buildDropdownHtml(suggestions, products, rescueData));
        });
      });
    }

    function handleInput() {
      if (state.debounceTimer) clearTimeout(state.debounceTimer);
      var query = input.value.trim();
      if (query.length < MIN_QUERY_LEN) {
        hideDropdown();
        return;
      }
      state.debounceTimer = setTimeout(triggerSearch, DEBOUNCE_MS);
    }

    document.addEventListener('click', function (e) {
      if (!state.isOpen) return;
      // Keep open if the click is on the input/form OR on the dropdown itself
      // (dropdown is now a body child, not inside searchWrapper).
      if (searchWrapper && searchWrapper.contains(e.target)) return;
      if (dropdown && dropdown.contains(e.target)) return;
      hideDropdown();
    });

    searchForm.addEventListener('submit', function (e) {
      if (state.isOpen && state.activeIndex >= 0) e.preventDefault();
      hideDropdown();
    });

    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', optionIdPrefix + '-listbox');
    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleKeydown);
    input.addEventListener('focus', function () {
      if (input.value.trim().length >= MIN_QUERY_LEN) triggerSearch();
    });
  }

  /* ------------------------------------------------------------------ */
  /* 6. FIND ALL INPUTS AND INIT                                         */
  /* ------------------------------------------------------------------ */

  var inputs = document.querySelectorAll('[data-predictive-search]');
  if (!inputs.length) {
    var heroInput = document.getElementById('hero-search');
    if (heroInput) {
      heroInput.setAttribute('data-predictive-search', '');
      inputs = [heroInput];
    }
  }
  for (var i = 0; i < inputs.length; i++) {
    initPredictiveSearch(inputs[i]);
  }

})();
